import * as Tabs from '@radix-ui/react-tabs'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'
import type { DropdownElement } from '../components/dropdown-filter'
import { DropdownFilter, isEitherToggleGroup } from '../components/dropdown-filter'
import { ToolIcon } from '../components/third-party-branding/tool-icon.tsx'
import { type Tool } from '../utils/api-client/api-schemas.ts'
import { getSelectableTools } from '../utils/detector-utils.ts'
import { getToolName, sortToolsByName } from '../utils/repository-utils.tsx'
import * as tabListStyles from './repository-details-page/repository-details-page.css.ts'
import type { ScanQuickViewId } from './repository-details-page/scan-quick-view-config'
import {
  parseScanView,
  patchSearchParams,
  ScansList,
  useScanQuickViewCounts,
} from './repository-details-page/scans-list.tsx'
import * as styles from './scans-overview-page.css.ts'

export function ScansOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedQuickView = parseScanView(searchParams.get('scan-view'))
  const { selectedTools, handleToolToggle, handleAllToolsToggle, onToggleAllFiltersOff } = useToolFilter(
    searchParams,
    setSearchParams
  )
  const tools = selectedTools.length > 0 ? selectedTools : undefined
  const scanQuickViewCounts = useScanQuickViewCounts({ tools })

  const onQuickViewChange = useCallback(
    (view: ScanQuickViewId) => {
      setSearchParams(prev => patchSearchParams(prev, { 'scan-view': view, 'page-number': null }))
    },
    [setSearchParams]
  )

  return (
    <ScansOverviewPagePure
      selectedQuickView={selectedQuickView}
      onQuickViewChange={onQuickViewChange}
      completedTotal={scanQuickViewCounts['scan-completed'] ?? 0}
      inProgressTotal={scanQuickViewCounts['scan-in-progress'] ?? 0}
      unanalyzedTotal={scanQuickViewCounts['scan-unanalyzed'] ?? 0}
      selectedTools={selectedTools}
      onToolToggle={handleToolToggle}
      onAllToolsToggle={handleAllToolsToggle}
      onToggleAllFiltersOff={onToggleAllFiltersOff}
      tools={tools}
    />
  )
}

export type ScansOverviewPagePureProps = {
  selectedQuickView: ScanQuickViewId
  onQuickViewChange: (view: ScanQuickViewId) => unknown
  completedTotal: number
  inProgressTotal: number
  unanalyzedTotal: number
  selectedTools: Tool[]
  onToolToggle: (tool: Tool) => unknown
  onAllToolsToggle: () => unknown
  onToggleAllFiltersOff: () => unknown
  tools?: Tool[]
}

export const ScansOverviewPagePure: React.FC<ScansOverviewPagePureProps> = ({
  selectedQuickView,
  onQuickViewChange,
  completedTotal,
  inProgressTotal,
  unanalyzedTotal,
  selectedTools,
  onToolToggle,
  onAllToolsToggle,
  onToggleAllFiltersOff,
  tools,
}) => {
  return (
    <div className={styles.scansOverviewBody}>
      <ScansOverviewHeader
        selectedTools={selectedTools}
        onToolToggle={onToolToggle}
        onAllToolsToggle={onAllToolsToggle}
        onToggleAllFiltersOff={onToggleAllFiltersOff}
      />
      <Tabs.Root
        value={selectedQuickView}
        onValueChange={value => {
          onQuickViewChange(value as ScanQuickViewId)
        }}
        className={styles.tabContainer}
      >
        <Tabs.List className={tabListStyles.tabList}>
          <div>
            <Tabs.Trigger className={styles.tab} value={'scan-completed' satisfies ScanQuickViewId}>
              ANALYZED &nbsp;<span className={styles.tabItemCount}>{completedTotal}</span>
            </Tabs.Trigger>
            <Tabs.Trigger className={styles.tab} value={'scan-in-progress' satisfies ScanQuickViewId}>
              IN PROGRESS &nbsp;<span className={styles.tabItemCount}>{inProgressTotal}</span>
            </Tabs.Trigger>
            <Tabs.Trigger className={styles.tab} value={'scan-unanalyzed' satisfies ScanQuickViewId}>
              UNANALYZED &nbsp;<span className={styles.tabItemCount}>{unanalyzedTotal}</span>
            </Tabs.Trigger>
          </div>
        </Tabs.List>

        <Tabs.Content className={styles.tabContent} value={selectedQuickView} tabIndex={-1} forceMount>
          <ScansList tools={tools} showRepositoryInfo variant="overview" />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  )
}

type ScansOverviewHeaderProps = {
  selectedTools: Tool[]
  onToolToggle: (tool: Tool) => unknown
  onAllToolsToggle: () => unknown
  onToggleAllFiltersOff: () => unknown
}

const ScansOverviewHeader: React.FC<ScansOverviewHeaderProps> = ({
  selectedTools,
  onToolToggle,
  onAllToolsToggle,
  onToggleAllFiltersOff,
}) => {
  const filterElements: DropdownElement[] = [
    {
      id: 'tools',
      text: 'Tools',
      filters: sortToolsByName(getSelectableTools()).map(tool => {
        const name = getToolName(tool)
        return {
          type: 'checkbox' as const,
          id: tool,
          text: name,
          value: selectedTools.includes(tool),
          onToggle: () => onToolToggle(tool),
          icon: <ToolIcon tool={tool} size="small" />,
        }
      }),
      onToggleGroup: onAllToolsToggle,
      type: 'group' as const,
    },
  ]

  const isAnyFilterActive = filterElements
    .filter(isEitherToggleGroup)
    .some(group => group.filters.some(filter => filter.value))

  return (
    <div className={styles.scansOverviewHeader} role="banner">
      <div className={styles.headerLeft}>
        <h4 className={styles.headerTitle}>Scans</h4>
        <p className={styles.headerParagraph}>Upload, triage and fix your scan findings.</p>
      </div>
      <DropdownFilter
        filterElements={filterElements}
        onToggleAllFiltersOff={onToggleAllFiltersOff}
        isAnyNonDefaultFilterActive={isAnyFilterActive}
      />
    </div>
  )
}

const readSelectedTools = (searchParams: URLSearchParams): Tool[] => {
  const selectableTools: readonly string[] = getSelectableTools()
  return searchParams.getAll('tools').filter((t): t is Tool => selectableTools.includes(t))
}

const useToolFilter = (searchParams: URLSearchParams, setSearchParams: ReturnType<typeof useSearchParams>[1]) => {
  const selectedTools = useMemo(() => readSelectedTools(searchParams), [searchParams])

  const handleToolToggle = useCallback(
    (tool: Tool) => {
      setSearchParams(prev => {
        const current = readSelectedTools(prev)
        const newTools = current.includes(tool) ? current.filter(t => t !== tool) : [...current, tool]
        return patchSearchParams(prev, { tools: newTools.length > 0 ? newTools : null, 'page-number': null })
      })
    },
    [setSearchParams]
  )

  const handleAllToolsToggle = useCallback(() => {
    setSearchParams(prev => {
      const newTools = readSelectedTools(prev).length === 0 ? getSelectableTools() : []
      return patchSearchParams(prev, { tools: newTools.length > 0 ? newTools : null, 'page-number': null })
    })
  }, [setSearchParams])

  const onToggleAllFiltersOff = useCallback(() => {
    setSearchParams(prev => patchSearchParams(prev, { tools: null, 'page-number': null }))
  }, [setSearchParams])

  return useMemo(
    () => ({
      selectedTools,
      handleToolToggle,
      handleAllToolsToggle,
      onToggleAllFiltersOff,
    }),
    [selectedTools, handleToolToggle, handleAllToolsToggle, onToggleAllFiltersOff]
  )
}
